import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { AuthService } from "../features/auth/services/auth.service";
import { LocalStorageService } from "../shared/services/local-storage.service";
import { environment } from "../../environments/environment";

type ApiKind = "doc" | "pdf" | "device" | "other" | "admin";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    public authService: AuthService,
    private router: Router,
    private localStorageService: LocalStorageService,
  ) {}

  /**
   * 1) Sans token -> garde + éventuelle redirection
   * 2) APIs spéciales (doc/pdf/device) -> SSO si dispo
   * 3) API back standard -> token + X-SSO-TOKEN si dispo
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const absoluteUrl = this.toAbsolute(request.url);
    const requestPath = this.toRequestPath(request.url);
    const ssoToken = this.authService.ssoSignal()?.token ?? null;
    const refreshToken = this.authService.ssoSignal()?.refreshToken ?? null;
    const hasToken = !!this.authService.tokenSignal();

    // --- 1) Cas SANS token applicatif ---
    const isAuthRoute = request.url.includes("/auth/login");
    const inDashboardProd = this.router.url.includes("dashboard_prod");
    const inLinkRoute = this.router.url.includes("/link/s");

    if (
      !inDashboardProd &&
      !hasToken &&
      !isAuthRoute &&
      !inLinkRoute &&
      !this.authService.popupOpened
    ) {
      this.localStorageService.putWithTTL("lastUrl", this.router.url, 1000);
      this.router.navigateByUrl("/auth/login");
      return next.handle(request.clone({ url: absoluteUrl }));
    }

    const target = this.whichApi(absoluteUrl);

    if (target === "admin") {
      if (requestPath === "/api/v1/auth/refresh" && refreshToken) {
        const ssoReq = request.clone({
          url: absoluteUrl,
          setHeaders: { Authorization: `Bearer ${refreshToken}` },
        });
        return next.handle(ssoReq);
      }
      const ssoReq = request.clone({
        url: absoluteUrl,
        setHeaders: { Authorization: `Bearer ${ssoToken}` },
      });
      return next.handle(ssoReq);
    }

    if (ssoToken && ["doc", "pdf", "device"].includes(target)) {
      const ssoReq = request.clone({
        url: absoluteUrl,
        setHeaders: { Authorization: `Bearer ${ssoToken}` },
      });
      return next.handle(ssoReq);
    }

    if (hasToken) {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${this.authService.tokenSignal()!}`,
      };
      if (ssoToken) {
        headers["X-SSO-TOKEN"] = `Bearer ${ssoToken}`;
      }
      const appReq = request.clone({ url: absoluteUrl, setHeaders: headers });
      return next.handle(appReq);
    }

    return next.handle(request);
  }

  private toAbsolute(input: string): string {
    const url = new URL(input, `${location.protocol}//${location.host}`);
    return url.toString();
  }

  private toRequestPath(input: string): string {
    const u = new URL(input, `${location.protocol}//${location.host}`);
    const path = (u.pathname || "/") + (u.search || "");
    return path.startsWith("/") ? path : `/${path}`;
  }

  toUrl(input: string): URL {
    const s = input.trim();
    if (s.startsWith("http")) {
      return new URL(s);
    }
    const origin = location?.origin;
    if (!origin) {
      throw new Error("location.origin.");
    }
    return new URL(s, origin);
  }

  private whichApi(urlStr: string): ApiKind {
    const bases = {
      doc: this.toUrl(environment.docApi()),
      pdf: this.toUrl(environment.pdfApi()),
      device: this.toUrl(environment.aristoteApi()),
      admin: this.toUrl(environment.adminApi()),
    };

    const candidate = new URL(urlStr, window.location.origin);

    const sameDomain = (candidate: URL, configUrl: URL) => {
      return candidate.href.startsWith(configUrl.href);
    };

    if (sameDomain(candidate, bases.doc)) return "doc";
    if (sameDomain(candidate, bases.pdf)) return "pdf";
    if (sameDomain(candidate, bases.device)) return "device";
    if (sameDomain(candidate, bases.admin) || urlStr === environment.adminApi())
      return "admin";
    return "other";
  }
}
